/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */
//@ts-nocheck
import React, { lazy, Suspense } from "react";
import {
  Route,
  Switch,
  Redirect,
  BrowserRouter,
  HashRouter,
  useHistory,
} from "react-router-dom";

import ScrollToTop from "./ScrollToTop";
import { routes } from "../services/appNavigatorService/config/routes.config.json";
import { EventBusModule } from "@vlinder-web/event-bus-module-react";
import { lazyWithRetry } from "./ComponentLoader";
import { PrivateRoute } from "@vlinder-web/private-route-widget-react";
import { StorageModule } from "@vlinder-web/storage-module-react";
import { StateOSD } from "@vlinder-web/state-osd-v2-widget-react";
import { VApplication } from "../application";
import { AppNavigatorService } from "../services";
import { useSMAppNavigator } from "./machines/pageContent";

//NOTE: Don't do lazy binding for landing page from v2 widget
import LandingPage from "../pages/Landing";
import toast from "react-hot-toast";
import { DEFAULT_APP_CONFIG } from "../config/default.config";
import { SwitchRolesPage } from "../pages/SwitchRoles/navigation";
import { ROLES_MODAL_OPEN } from "../pages/SwitchRoles/container/container.com";
import { RouteWithSidebar } from "../widgets/route-with-sidebar-widget-react-main";
import PrintLabelPage from "../pages/PrintLabels";
import BlackListUserListPage from "../pages/BlackListUsers";
import BlockListUserListPage from "../pages/BlockListUsers";

const SignInPage = lazyWithRetry(() => import("../pages/SignIn"));

const BulkImportCommitteListPage = lazyWithRetry(
  () => import("../pages/BulkImportCommitteList")
);
const PresentationImportPage = lazyWithRetry(
  () => import("../pages/PresentationImport")
);
const ShelfDetailPage = lazyWithRetry(() => import("../pages/ShelfDetail"));
const BandRouteMapPage = lazyWithRetry(() => import("../pages/BandRouteMap"));
const BandRouteLiveMapPage = lazyWithRetry(() => import("../pages/BandRouteLiveMap"));
const ShelfListPage = lazyWithRetry(() => import("../pages/ShelfList"));
const OpenSigninPage = lazyWithRetry(() => import("../pages/OpenLogin"));
const OpenUpdateProfile = lazyWithRetry(
  () => import("../pages/OpenProfileUpdate")
);
const SalesReportPage = lazyWithRetry(() => import("../pages/SalesReport"));
const HolderBatchImportPage = lazyWithRetry(
  () => import("../pages/HoldersBatchImport")
);
const HolderBatchCommitteImportPage = lazyWithRetry(
  () => import("../pages/HoldersBatchCommitteImport")
);

const TokenExpiryPage = lazyWithRetry(() => import("../pages/TokenExpiry"));
const InviteesListPage = lazyWithRetry(() => import("../pages/InviteesList"));
const BulkImportUserListPage = lazyWithRetry(
  () => import("../pages/BulkImportUserList")
);
const GenerateDiscountPage = lazyWithRetry(
  () => import("../pages/GenerateDiscount")
);
const DiscountListPage = lazyWithRetry(() => import("../pages/DiscountList"));

const CommitteeDiscountListPage = lazyWithRetry(
  () => import("../pages/CommitteeDiscountList")
);

const DiscountCouponListPage = lazyWithRetry(
  () => import("../pages/DiscountCouponList")
);

const OTPTemplatePage = lazyWithRetry(() => import("../pages/EmailTemplate"));

const MasqInviteEmailTemplatePage = lazyWithRetry(
  () => import("../pages/MasqInviteEmailTemplate")
);

const MasqInviteWithDiscountEmailTemplatePage = lazyWithRetry(
  () => import("../pages/MasqInviteWithDiscountEmailTemplate")
);

const InvoiceEmailTemplatePage = lazyWithRetry(
  () => import("../pages/InvoiceEmailTemplate")
);
const CreateDiscountPage = lazyWithRetry(
  () => import("../pages/CreateDiscount")
);
const TransferBoxNewUserEmailTemplatePage = lazyWithRetry(
  () => import("../pages/TransferBoxNewUserEmailTemplate")
);

const TransferBoxOldUserEmailTemplatePage = lazyWithRetry(
  () => import("../pages/TransferBoxOldUserEmailTemplate")
);

const APV2PaymentDetails = lazyWithRetry(
  () => import("../pages/APV2PaymentDetails")
);

const CommonEmailVerificationTemplatePage = lazyWithRetry(
  () => import("../pages/CommonEmailVerificationTemplate")
);

const CommitteeInviteEmailTemplatePage = lazyWithRetry(
  () => import("../pages/CommitteeInviteEmailTemplate")
);

const CashSupervisorInviteEmailTemplatePage = lazyWithRetry(
  () => import("../pages/CashSupervisorEmailTemplate")
);

const SupervisorEmailTemplatePage = lazyWithRetry(
  () => import("../pages/SupervisorEmailTemplate")
);

const MasqDownPaymentEmailTemplatePage = lazyWithRetry(
  () => import("../pages/MasqDownPaymentEmailTemplate")
);
const DeleteBoxEmailTemplatePage = lazyWithRetry(
  () => import("../pages/DeleteBoxEmailTemplate")
);

const AdminInviteEmailTemplatePage = lazyWithRetry(
  () => import("../pages/AdminInviteEmailTemplate")
);

const DiscountInviteEmailTemplatePage = lazyWithRetry(
  () => import("../pages/DiscountInviteEmailTemplate")
);

const CashierInviteEmailTemplatePage = lazyWithRetry(
  () => import("../pages/CashierInviteEmailTemplate")
);

const CostumePurchaseInviteEmailTemplatePage = lazyWithRetry(
  () => import("../pages/CostumePurchaseInviteEmailTemplate")
);

const PreBookInviteEmailTemplatePage = lazyWithRetry(
  () => import("../pages/PreBookInviteEmailTemplate")
);

const PartialRemainderEmailTemplatePage = lazyWithRetry(
  () => import("../pages/PartialRemainderEmailTemplate")
);

const UnPaidRemainderEmailTemplatePage = lazyWithRetry(
  () => import("../pages/UnPaidRemainderEmailTemplate")
);

const ScheduleTablePage = lazyWithRetry(() => import("../pages/ScheduleTable"));

const ScheduleListPage = lazyWithRetry(() => import("../pages/ScheduleList"));

const CashierPaymentPage = lazyWithRetry(
  () => import("../pages/CashierPayment")
);

const PreBookDownPaymentEmailTemplatePage = lazyWithRetry(
  () => import("../pages/PreBookDownPaymentEmailTemplate")
);

const PaymentReportPage = lazyWithRetry(() => import("../pages/PaymentReport"));
const DeliveredReportPage = lazyWithRetry(
  () => import("../pages/DeliveredReport")
);
const SettingsPage = lazyWithRetry(() => import("../pages/Settings"));
const ButtonSettingsPage = lazyWithRetry(
  () => import("../pages/ButtonSettings")
);
const CashierPurchasedCostumePage = lazyWithRetry(
  () => import("../pages/CashierPurchasedCostume")
);
const MasqBoxListPage = lazyWithRetry(() => import("../pages/MasqBoxList"));
const MasqViewBoxPage = lazyWithRetry(() => import("../pages/MasqViewBox"));
const InvoicePDFTemplate = lazyWithRetry(
  () => import("../pages/InvoicePDFEmailTemplate")
);
// const OpenRegisterPage = lazyWithRetry(
// () => import("../pages/OpenRegistration")
// );

const ZeroPaymentReportPage = lazyWithRetry(
  () => import("../pages/ZeroPaymentReport")
);

const PartialPaymentReportPage = lazyWithRetry(
  () => import("../pages/PartialPaymentReport")
);
const FullPaymentReportPage = lazyWithRetry(
  () => import("../pages/FullPaymentReport")
);
const DiscountReportPage = lazyWithRetry(
  () => import("../pages/DiscountReport")
);
const CouponReportPage = lazyWithRetry(() => import("../pages/CouponReport"));
const CommitteListPage = lazyWithRetry(() => import("../pages/CommitteList"));
const CashierListPage = lazyWithRetry(() => import("../pages/CashierList"));
const MyProfilePage = lazyWithRetry(() => import("../pages/MyProfile"));
const OrgProfilePage = lazyWithRetry(() => import("../pages/OrgProfile"));
const InventoryControlPage = lazyWithRetry(
  () => import("../pages/InventoryControl")
);
const CostumeInventoryReportPage = lazyWithRetry(
  () => import("../pages/CostumeInventoryReport")
);

const PresentationPage = lazyWithRetry(() => import("../pages/Presentation"));
const CostumePage = lazyWithRetry(() => import("../pages/Costume"));
const CostumeOpenRegPage = lazyWithRetry(
  () => import("../pages/CostumeOpenreg")
);
const OpenRegistrationCostumePage = lazyWithRetry(
  () => import("../pages/OpenRegistrationCostume")
);
const PurchasedCostumePage = lazyWithRetry(
  () => import("../pages/PurchasedCostume")
);

const PurchasedCostumeListPage = lazyWithRetry(
  () => import("../pages/PurchasedCostumeList")
);

const AboutMePage = lazyWithRetry(() => import("../pages/AboutMe"));

//ErrorPages
const NotFoundPage = lazyWithRetry(() => import("../pages/Notfound"));
const ServerErrorPage = lazyWithRetry(() => import("../pages/ServerError"));

const UsersListPage = lazyWithRetry(() => import("../pages/UsersList"));

const MasqueraderListPage = lazyWithRetry(
  () => import("../pages/MasqueraderList")
);
const PresentationListPage = lazyWithRetry(
  () => import("../pages/PresentationList")
);
const InviteMasqueraderPage = lazyWithRetry(
  () => import("../pages/InviteMasquerader")
);
const PaymentFailedFACPage = lazyWithRetry(
  () => import("../pages/PaymentFailedFAC")
);
const PurchasedCostumeDetailsPage = lazyWithRetry(
  () => import("../pages/PurchasedCostumeDetail")
);
const ManageOpenRegistrationPage = lazyWithRetry(
  () => import("../pages/ManageOpenRegistration")
);

const SectionOptionInventoryPage = lazyWithRetry(
  () => import("../pages/SectionOptionInventory")
);
const SectionBasePriceSummaryPage = lazyWithRetry(
  () => import("../pages/SectionBasePriceSummary")
);
const SendInviteCommiteePage = lazyWithRetry(
  () => import("../pages/SendInviteCommitee")
);
const CommitteMemberReportPage = lazyWithRetry(
  () => import("../pages/CommitteMemberReport")
);
const MasqueraderReportPage = lazyWithRetry(
  () => import("../pages/MasqueraderReport")
);

const ViewBoxDetailPage = lazyWithRetry(
  () => import("../pages/ViewBoxDetails")
);
const SectionOptInvReportPage = lazyWithRetry(
  () => import("../pages/SectionOptInvReport")
);
const ViewMasqBoxDetailsPage = lazyWithRetry(
  () => import("../pages/ViewMasqBoxDetails")
);

const ChangeCostumeOptionsPage = lazyWithRetry(
  () => import("../pages/ChangeCostumeOptions")
);

const SwitchSectionPage = lazyWithRetry(() => import("../pages/SwitchSection"));
const DeletedBoxPage = lazyWithRetry(() => import("../pages/DeletedBox"));
const UpdatedBoxPage = lazyWithRetry(() => import("../pages/UpdatedBox"));
const DeletedViewBoxPage = lazyWithRetry(
  () => import("../pages/DeletedViewBox")
);

const DiscountedMasqueraderPage = lazyWithRetry(
  () => import("../pages/DiscountedMasquerader")
);
const CreateMasqBoxPage = lazyWithRetry(() => import("../pages/CreateMasqBox"));
const DiscountCodeEmailTemplatePage = lazyWithRetry(
  () => import("../pages/DiscountCodeEmailTemplate")
);

// Payment Status Pages
const PaymentSuccessPage = lazyWithRetry(
  () => import("../pages/PaymentSuccess")
);

const BoxDetailPage = lazyWithRetry(() => import("../pages/BoxDetail"));

const PaymentFailedPage = lazyWithRetry(() => import("../pages/PaymentFailed"));

const PaymentPendingPage = lazyWithRetry(
  () => import("../pages/PaymentPending")
);

const PaymentTransactionPage = lazyWithRetry(
  () => import("../pages/PaymentTransaction")
);
const ProfileUpdateStepperPage = lazyWithRetry(
  () => import("../pages/ProfileUpdateStepper")
);

const BlackListUserPage = lazyWithRetry(
  () => import("../pages/BlackListUsers")
);

const PrebookToActualBoxesPages = lazyWithRetry(
  () => import("../pages/PrebookToActualBoxes")
);

const BlockListUserPage = lazyWithRetry(
  () => import("../pages/BlockListUsers")
);

const getAppNavService = () => {
  const app = VApplication.getInstance();
  return app?.getSync("services.AppNavigatorService") as AppNavigatorService;
};

const RouterConfig = () => {
  const { appNavigatorSend, appNavigatorState, appNavigatorMachine } =
    useSMAppNavigator({});
  const appNavSrv = getAppNavService();
  const history = useHistory();

  const _handleDropDownItemClick = (item: any) => {
    const storageSrv = StorageModule?.getInstance();
    const eventBus = EventBusModule?.getInstance()?.getEventBus();

    console.log("on DD Item click =>", item);
    if (item.id === "logout") {
      storageSrv.reset("session");
      storageSrv.reset("local");
      window.open(`/`, "_self");
    } else if (item.id === "switchRoles") {
      eventBus.emit(ROLES_MODAL_OPEN, null, {});
    } else if (item.id === "myProfile") {
      history.push(routes?.MyProfile?.path);
    } else {
      toast.success("Coming soon!");
    }
  };

  if (appNavigatorState?.matches("RENDER_PAGE")) {
    const {
      // brandLogo,
      brandLogoLink,
      brandTitle,
      sidebarItems,
      routes,
      copyrightStartYear,
      copyrightTitle,
      brandDetails,
      vlinderDetails,
      whoami,
      userName,
      userImage,
      dropdownItems,
      footerSection,
    } = { ...(appNavigatorState?.event?.data?.value || {}) };

    const storageSrv = StorageModule.getInstance();
    const localStorage = storageSrv?.get("theme", "local");
    const brandLogo = localStorage?.bandLogo;
    console.log("brandLogo", brandLogo);
    return (
      <Switch>
        <RouteWithSidebar
          exact
          path={routes?.MyProfile?.path}
          component={MyProfilePage}
          items={sidebarItems}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          userName={userName}
          copyrightStartYear={copyrightStartYear}
          copyrightTitle={copyrightTitle}
          brandDetails={brandDetails}
          vlinderDetails={vlinderDetails}
          userImage={userImage}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.MyProfile?.id,
            whoami,
            "read"
          )}
          brandLogo={brandLogo}
          brandTitle={brandTitle}
        />
        <RouteWithSidebar
          exact
          path={routes?.GenerateDiscount?.path}
          component={GenerateDiscountPage}
          items={sidebarItems}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          userName={userName}
          copyrightStartYear={copyrightStartYear}
          copyrightTitle={copyrightTitle}
          brandDetails={brandDetails}
          vlinderDetails={vlinderDetails}
          userImage={userImage}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.GenerateDiscount?.id,
            whoami,
            "read"
          )}
          brandLogo={brandLogo}
          brandTitle={brandTitle}
        />
        <RouteWithSidebar
          exact
          path={routes?.PrebookToActualBoxes?.path}
          component={PrebookToActualBoxesPages}
          items={sidebarItems}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          userName={userName}
          copyrightStartYear={copyrightStartYear}
          copyrightTitle={copyrightTitle}
          brandDetails={brandDetails}
          vlinderDetails={vlinderDetails}
          userImage={userImage}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.PrebookToActualBoxes?.id,
            whoami,
            "read"
          )}
          brandLogo={brandLogo}
          brandTitle={brandTitle}
        />
        <RouteWithSidebar
          exact
          path={routes?.DiscountCodeTemplate?.path}
          component={DiscountCodeEmailTemplatePage}
          items={sidebarItems}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          userName={userName}
          copyrightStartYear={copyrightStartYear}
          copyrightTitle={copyrightTitle}
          brandDetails={brandDetails}
          vlinderDetails={vlinderDetails}
          userImage={userImage}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.DiscountCodeTemplate?.id,
            whoami,
            "read"
          )}
          brandLogo={brandLogo}
          brandTitle={brandTitle}
        />
        <RouteWithSidebar
          exact
          path={routes?.DiscountList?.path}
          component={DiscountListPage}
          items={sidebarItems}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          userName={userName}
          copyrightStartYear={copyrightStartYear}
          copyrightTitle={copyrightTitle}
          brandDetails={brandDetails}
          vlinderDetails={vlinderDetails}
          userImage={userImage}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.DiscountList?.id,
            whoami,
            "read"
          )}
          brandLogo={brandLogo}
          brandTitle={brandTitle}
        />
        <RouteWithSidebar
          exact
          path={routes?.CommDisList?.path}
          component={CommitteeDiscountListPage}
          items={sidebarItems}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          userName={userName}
          copyrightStartYear={copyrightStartYear}
          copyrightTitle={copyrightTitle}
          brandDetails={brandDetails}
          vlinderDetails={vlinderDetails}
          userImage={userImage}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.CommDisList?.id,
            whoami,
            "read"
          )}
          brandLogo={brandLogo}
          brandTitle={brandTitle}
        />
        <RouteWithSidebar
          exact
          path={routes?.DiscountCouponList?.path}
          component={DiscountCouponListPage}
          items={sidebarItems}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          userName={userName}
          copyrightStartYear={copyrightStartYear}
          copyrightTitle={copyrightTitle}
          brandDetails={brandDetails}
          vlinderDetails={vlinderDetails}
          userImage={userImage}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.DiscountCouponList?.id,
            whoami,
            "read"
          )}
          brandLogo={brandLogo}
          brandTitle={brandTitle}
        />

        <RouteWithSidebar
          exact
          path={routes?.CashierPayment?.path}
          component={CashierPaymentPage}
          items={sidebarItems}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          userName={userName}
          copyrightStartYear={copyrightStartYear}
          copyrightTitle={copyrightTitle}
          brandDetails={brandDetails}
          vlinderDetails={vlinderDetails}
          userImage={userImage}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.CashierPayment?.id,
            whoami,
            "read"
          )}
          brandLogo={brandLogo}
          brandTitle={brandTitle}
        />
        <RouteWithSidebar
          exact
          path={routes?.MasqBoxList?.path}
          component={MasqBoxListPage}
          items={sidebarItems}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          userName={userName}
          copyrightStartYear={copyrightStartYear}
          copyrightTitle={copyrightTitle}
          brandDetails={brandDetails}
          vlinderDetails={vlinderDetails}
          userImage={userImage}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.MasqBoxList?.id,
            whoami,
            "read"
          )}
          brandLogo={brandLogo}
          brandTitle={brandTitle}
        />
        <RouteWithSidebar
          exact
          path={routes?.MasqViewBox?.path}
          component={MasqViewBoxPage}
          items={sidebarItems}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          userName={userName}
          copyrightStartYear={copyrightStartYear}
          copyrightTitle={copyrightTitle}
          brandDetails={brandDetails}
          vlinderDetails={vlinderDetails}
          userImage={userImage}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.MasqViewBox?.id,
            whoami,
            "read"
          )}
          brandLogo={brandLogo}
          brandTitle={brandTitle}
        />
        <RouteWithSidebar
          exact
          path={routes?.CashierPurchasedCostume?.path}
          component={CashierPurchasedCostumePage}
          items={sidebarItems}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          userName={userName}
          copyrightStartYear={copyrightStartYear}
          copyrightTitle={copyrightTitle}
          brandDetails={brandDetails}
          vlinderDetails={vlinderDetails}
          userImage={userImage}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.CashierPurchasedCostume?.id,
            whoami,
            "read"
          )}
          brandLogo={brandLogo}
          brandTitle={brandTitle}
        />
        <RouteWithSidebar
          exact
          path={routes?.InvoicePDFTemplate?.path}
          component={InvoicePDFTemplate}
          items={sidebarItems}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          userName={userName}
          copyrightStartYear={copyrightStartYear}
          copyrightTitle={copyrightTitle}
          brandDetails={brandDetails}
          vlinderDetails={vlinderDetails}
          userImage={userImage}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.InvoicePDFTemplate?.id,
            whoami,
            "read"
          )}
          brandLogo={brandLogo}
          brandTitle={brandTitle}
        />
        <RouteWithSidebar
          exact
          path={routes?.CreateDiscount?.path}
          component={CreateDiscountPage}
          items={sidebarItems}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          userName={userName}
          copyrightStartYear={copyrightStartYear}
          copyrightTitle={copyrightTitle}
          brandDetails={brandDetails}
          vlinderDetails={vlinderDetails}
          userImage={userImage}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.CreateDiscount?.id,
            whoami,
            "read"
          )}
          brandLogo={brandLogo}
          brandTitle={brandTitle}
        />
        <RouteWithSidebar
          exact
          path={routes?.SectionBasePriceSummary?.path}
          component={SectionBasePriceSummaryPage}
          items={sidebarItems}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          userName={userName}
          copyrightStartYear={copyrightStartYear}
          copyrightTitle={copyrightTitle}
          brandDetails={brandDetails}
          vlinderDetails={vlinderDetails}
          userImage={userImage}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.SectionBasePriceSummary?.id,
            whoami,
            "read"
          )}
          brandLogo={brandLogo}
          brandTitle={brandTitle}
        />
        <RouteWithSidebar
          exact
          path={routes?.ThemeAndLogo?.path}
          component={SettingsPage}
          items={sidebarItems}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          userName={userName}
          copyrightStartYear={copyrightStartYear}
          copyrightTitle={copyrightTitle}
          brandDetails={brandDetails}
          vlinderDetails={vlinderDetails}
          userImage={userImage}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.ThemeAndLogo?.id,
            whoami,
            "read"
          )}
          brandLogo={brandLogo}
          brandTitle={brandTitle}
        />
        <RouteWithSidebar
          exact
          path={routes?.ButtonSettings?.path}
          component={ButtonSettingsPage}
          items={sidebarItems}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          userName={userName}
          copyrightStartYear={copyrightStartYear}
          copyrightTitle={copyrightTitle}
          brandDetails={brandDetails}
          vlinderDetails={vlinderDetails}
          userImage={userImage}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.ButtonSettings?.id,
            whoami,
            "read"
          )}
          brandLogo={brandLogo}
          brandTitle={brandTitle}
        />
        <RouteWithSidebar
          exact
          path={routes?.CostumeInventoryReport?.path}
          component={CostumeInventoryReportPage}
          items={sidebarItems}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          userName={userName}
          copyrightStartYear={copyrightStartYear}
          copyrightTitle={copyrightTitle}
          brandDetails={brandDetails}
          vlinderDetails={vlinderDetails}
          userImage={userImage}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.CostumeInventoryReport?.id,
            whoami,
            "read"
          )}
          brandLogo={brandLogo}
          brandTitle={brandTitle}
        />
        <RouteWithSidebar
          exact
          path={routes?.ShelfList?.path}
          component={ShelfListPage}
          items={sidebarItems}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          userName={userName}
          copyrightStartYear={copyrightStartYear}
          copyrightTitle={copyrightTitle}
          brandDetails={brandDetails}
          vlinderDetails={vlinderDetails}
          userImage={userImage}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.ShelfList?.id,
            whoami,
            "read"
          )}
          brandLogo={brandLogo}
          brandTitle={brandTitle}
        />
        <RouteWithSidebar
          exact
          path={routes?.OTPTemplate?.path}
          component={OTPTemplatePage}
          items={sidebarItems}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          userName={userName}
          copyrightStartYear={copyrightStartYear}
          copyrightTitle={copyrightTitle}
          brandDetails={brandDetails}
          vlinderDetails={vlinderDetails}
          userImage={userImage}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.OTPTemplate?.id,
            whoami,
            "read"
          )}
          brandLogo={brandLogo}
          brandTitle={brandTitle}
        />
        <RouteWithSidebar
          exact
          path={routes?.MasqInvitationWithDiscountTemplate?.path}
          component={MasqInviteWithDiscountEmailTemplatePage}
          items={sidebarItems}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          userName={userName}
          copyrightStartYear={copyrightStartYear}
          copyrightTitle={copyrightTitle}
          brandDetails={brandDetails}
          vlinderDetails={vlinderDetails}
          userImage={userImage}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.MasqInvitationWithDiscountTemplate?.id,
            whoami,
            "read"
          )}
          brandLogo={brandLogo}
          brandTitle={brandTitle}
        />
        <RouteWithSidebar
          exact
          path={routes?.MasqInvitationTemplate?.path}
          component={MasqInviteEmailTemplatePage}
          items={sidebarItems}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          userName={userName}
          copyrightStartYear={copyrightStartYear}
          copyrightTitle={copyrightTitle}
          brandDetails={brandDetails}
          vlinderDetails={vlinderDetails}
          userImage={userImage}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.MasqInvitationTemplate?.id,
            whoami,
            "read"
          )}
          brandLogo={brandLogo}
          brandTitle={brandTitle}
        />
        <RouteWithSidebar
          exact
          path={routes?.InvoiceTemplate?.path}
          component={InvoiceEmailTemplatePage}
          items={sidebarItems}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          userName={userName}
          copyrightStartYear={copyrightStartYear}
          copyrightTitle={copyrightTitle}
          brandDetails={brandDetails}
          vlinderDetails={vlinderDetails}
          userImage={userImage}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.InvoiceTemplate?.id,
            whoami,
            "read"
          )}
          brandLogo={brandLogo}
          brandTitle={brandTitle}
        />
        <RouteWithSidebar
          exact
          path={routes?.TransferBoxNewUserTemplate?.path}
          component={TransferBoxNewUserEmailTemplatePage}
          items={sidebarItems}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          userName={userName}
          copyrightStartYear={copyrightStartYear}
          copyrightTitle={copyrightTitle}
          brandDetails={brandDetails}
          vlinderDetails={vlinderDetails}
          userImage={userImage}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.TransferBoxNewUserTemplate?.id,
            whoami,
            "read"
          )}
          brandLogo={brandLogo}
          brandTitle={brandTitle}
        />
        <RouteWithSidebar
          exact
          path={routes?.TransferBoxOldUserTemplate?.path}
          component={TransferBoxOldUserEmailTemplatePage}
          items={sidebarItems}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          userName={userName}
          copyrightStartYear={copyrightStartYear}
          copyrightTitle={copyrightTitle}
          brandDetails={brandDetails}
          vlinderDetails={vlinderDetails}
          userImage={userImage}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.TransferBoxOldUserTemplate?.id,
            whoami,
            "read"
          )}
          brandLogo={brandLogo}
          brandTitle={brandTitle}
        />
        <RouteWithSidebar
          exact
          path={routes?.CommonEmailVerificationTemplate?.path}
          component={CommonEmailVerificationTemplatePage}
          items={sidebarItems}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          userName={userName}
          copyrightStartYear={copyrightStartYear}
          copyrightTitle={copyrightTitle}
          brandDetails={brandDetails}
          vlinderDetails={vlinderDetails}
          userImage={userImage}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.CommonEmailVerificationTemplate?.id,
            whoami,
            "read"
          )}
          brandLogo={brandLogo}
          brandTitle={brandTitle}
        />
        <RouteWithSidebar
          exact
          path={routes?.CashSupervisorInvitationTemplate?.path}
          component={CashSupervisorInviteEmailTemplatePage}
          items={sidebarItems}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          userName={userName}
          copyrightStartYear={copyrightStartYear}
          copyrightTitle={copyrightTitle}
          brandDetails={brandDetails}
          vlinderDetails={vlinderDetails}
          userImage={userImage}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.CashSupervisorInvitationTemplate?.id,
            whoami,
            "read"
          )}
          brandLogo={brandLogo}
          brandTitle={brandTitle}
        />
        <RouteWithSidebar
          exact
          path={routes?.SupervisorInvitationTemplate?.path}
          component={SupervisorEmailTemplatePage}
          items={sidebarItems}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          userName={userName}
          copyrightStartYear={copyrightStartYear}
          copyrightTitle={copyrightTitle}
          brandDetails={brandDetails}
          vlinderDetails={vlinderDetails}
          userImage={userImage}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.SupervisorInvitationTemplate?.id,
            whoami,
            "read"
          )}
          brandLogo={brandLogo}
          brandTitle={brandTitle}
        />
        <RouteWithSidebar
          exact
          path={routes?.CommitteeInvitationTemplate?.path}
          component={CommitteeInviteEmailTemplatePage}
          items={sidebarItems}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          userName={userName}
          copyrightStartYear={copyrightStartYear}
          copyrightTitle={copyrightTitle}
          brandDetails={brandDetails}
          vlinderDetails={vlinderDetails}
          userImage={userImage}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.CommitteeInvitationTemplate?.id,
            whoami,
            "read"
          )}
          brandLogo={brandLogo}
          brandTitle={brandTitle}
        />
        <RouteWithSidebar
          exact
          path={routes?.PreBookDownPaymentEmailTemplate?.path}
          component={PreBookDownPaymentEmailTemplatePage}
          items={sidebarItems}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          userName={userName}
          copyrightStartYear={copyrightStartYear}
          copyrightTitle={copyrightTitle}
          brandDetails={brandDetails}
          vlinderDetails={vlinderDetails}
          userImage={userImage}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.PreBookDownPaymentEmailTemplate?.id,
            whoami,
            "read"
          )}
          brandLogo={brandLogo}
          brandTitle={brandTitle}
        />
        <RouteWithSidebar
          exact
          path={routes?.MasqDownPaymentTemplate?.path}
          component={MasqDownPaymentEmailTemplatePage}
          items={sidebarItems}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          userName={userName}
          copyrightStartYear={copyrightStartYear}
          copyrightTitle={copyrightTitle}
          brandDetails={brandDetails}
          vlinderDetails={vlinderDetails}
          userImage={userImage}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.MasqDownPaymentTemplate?.id,
            whoami,
            "read"
          )}
          brandLogo={brandLogo}
          brandTitle={brandTitle}
        />
        <RouteWithSidebar
          exact
          path={routes?.DeletedBoxTemplate?.path}
          component={DeleteBoxEmailTemplatePage}
          items={sidebarItems}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          userName={userName}
          copyrightStartYear={copyrightStartYear}
          copyrightTitle={copyrightTitle}
          brandDetails={brandDetails}
          vlinderDetails={vlinderDetails}
          userImage={userImage}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.DeletedBoxTemplate?.id,
            whoami,
            "read"
          )}
          brandLogo={brandLogo}
          brandTitle={brandTitle}
        />
        <RouteWithSidebar
          exact
          path={routes?.UpdatedBox?.path}
          component={UpdatedBoxPage}
          items={sidebarItems}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          userName={userName}
          copyrightStartYear={copyrightStartYear}
          copyrightTitle={copyrightTitle}
          brandDetails={brandDetails}
          vlinderDetails={vlinderDetails}
          userImage={userImage}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.UpdatedBox?.id,
            whoami,
            "read"
          )}
          brandLogo={brandLogo}
          brandTitle={brandTitle}
        />
        <RouteWithSidebar
          exact
          path={routes?.AdminInvitationTemplate?.path}
          component={AdminInviteEmailTemplatePage}
          items={sidebarItems}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          userName={userName}
          copyrightStartYear={copyrightStartYear}
          copyrightTitle={copyrightTitle}
          brandDetails={brandDetails}
          vlinderDetails={vlinderDetails}
          userImage={userImage}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.AdminInvitationTemplate?.id,
            whoami,
            "read"
          )}
          brandLogo={brandLogo}
          brandTitle={brandTitle}
        />
        <RouteWithSidebar
          exact
          path={routes?.DiscountInviteTemplate?.path}
          component={DiscountInviteEmailTemplatePage}
          items={sidebarItems}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          userName={userName}
          copyrightStartYear={copyrightStartYear}
          copyrightTitle={copyrightTitle}
          brandDetails={brandDetails}
          vlinderDetails={vlinderDetails}
          userImage={userImage}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.DiscountInviteTemplate?.id,
            whoami,
            "read"
          )}
          brandLogo={brandLogo}
          brandTitle={brandTitle}
        />
        <RouteWithSidebar
          exact
          path={routes?.PurchasedCostumeDetails?.path}
          component={PurchasedCostumeDetailsPage}
          items={sidebarItems}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          userName={userName}
          copyrightStartYear={copyrightStartYear}
          copyrightTitle={copyrightTitle}
          brandDetails={brandDetails}
          vlinderDetails={vlinderDetails}
          userImage={userImage}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.PurchasedCostumeDetails?.id,
            whoami,
            "read"
          )}
          brandLogo={brandLogo}
          brandTitle={brandTitle}
        />
        <RouteWithSidebar
          exact
          path={routes?.CashierInviteTemplate?.path}
          component={CashierInviteEmailTemplatePage}
          items={sidebarItems}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          userName={userName}
          copyrightStartYear={copyrightStartYear}
          copyrightTitle={copyrightTitle}
          brandDetails={brandDetails}
          vlinderDetails={vlinderDetails}
          userImage={userImage}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.CashierInviteTemplate?.id,
            whoami,
            "read"
          )}
          brandLogo={brandLogo}
          brandTitle={brandTitle}
        />
        <RouteWithSidebar
          exact
          path={routes?.CostumePurchaseInviteTemplate?.path}
          component={CostumePurchaseInviteEmailTemplatePage}
          items={sidebarItems}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          userName={userName}
          copyrightStartYear={copyrightStartYear}
          copyrightTitle={copyrightTitle}
          brandDetails={brandDetails}
          vlinderDetails={vlinderDetails}
          userImage={userImage}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.CostumePurchaseInviteTemplate?.id,
            whoami,
            "read"
          )}
          brandLogo={brandLogo}
          brandTitle={brandTitle}
        />
        <RouteWithSidebar
          exact
          path={routes?.ShelfDetail?.path}
          component={ShelfDetailPage}
          items={sidebarItems}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          userName={userName}
          copyrightStartYear={copyrightStartYear}
          copyrightTitle={copyrightTitle}
          brandDetails={brandDetails}
          vlinderDetails={vlinderDetails}
          userImage={userImage}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.ShelfDetail?.id,
            whoami,
            "read"
          )}
          brandLogo={brandLogo}
          brandTitle={brandTitle}
        />
        <RouteWithSidebar
          exact
          path={routes?.BoxDetail?.path}
          component={BoxDetailPage}
          items={sidebarItems}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          userName={userName}
          copyrightStartYear={copyrightStartYear}
          copyrightTitle={copyrightTitle}
          brandDetails={brandDetails}
          vlinderDetails={vlinderDetails}
          userImage={userImage}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.BoxDetail?.id,
            whoami,
            "read"
          )}
          brandLogo={brandLogo}
          brandTitle={brandTitle}
        />
        <RouteWithSidebar
          exact
          path={routes?.ScheduleList?.path}
          component={ScheduleListPage}
          items={sidebarItems}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          userName={userName}
          copyrightStartYear={copyrightStartYear}
          copyrightTitle={copyrightTitle}
          brandDetails={brandDetails}
          vlinderDetails={vlinderDetails}
          userImage={userImage}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.ScheduleList?.id,
            whoami,
            "read"
          )}
          brandLogo={brandLogo}
          brandTitle={brandTitle}
        />
        <RouteWithSidebar
          exact
          path={routes?.ScheduleTable?.path}
          component={ScheduleTablePage}
          items={sidebarItems}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          userName={userName}
          copyrightStartYear={copyrightStartYear}
          copyrightTitle={copyrightTitle}
          brandDetails={brandDetails}
          vlinderDetails={vlinderDetails}
          userImage={userImage}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.ScheduleTable?.id,
            whoami,
            "read"
          )}
          brandLogo={brandLogo}
          brandTitle={brandTitle}
        />
        <RouteWithSidebar
          exact
          path={routes?.OrgProfile?.path}
          component={OrgProfilePage}
          items={sidebarItems}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          userName={userName}
          copyrightStartYear={copyrightStartYear}
          copyrightTitle={copyrightTitle}
          brandDetails={brandDetails}
          vlinderDetails={vlinderDetails}
          userImage={userImage}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.OrgProfile?.id,
            whoami,
            "read"
          )}
          brandLogo={brandLogo}
          brandTitle={brandTitle}
        />
        <RouteWithSidebar
          exact
          path={routes?.InviteesList?.path}
          component={InviteesListPage}
          items={sidebarItems}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          userName={userName}
          copyrightStartYear={copyrightStartYear}
          copyrightTitle={copyrightTitle}
          brandDetails={brandDetails}
          vlinderDetails={vlinderDetails}
          userImage={userImage}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.InviteesList?.id,
            whoami,
            "read"
          )}
          brandLogo={brandLogo}
          brandTitle={brandTitle}
        />
        <RouteWithSidebar
          exact
          path={routes?.PaymentReport?.path}
          component={PaymentReportPage}
          items={sidebarItems}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          userName={userName}
          copyrightStartYear={copyrightStartYear}
          copyrightTitle={copyrightTitle}
          brandDetails={brandDetails}
          vlinderDetails={vlinderDetails}
          userImage={userImage}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.PaymentReport?.id,
            whoami,
            "read"
          )}
          brandLogo={brandLogo}
          brandTitle={brandTitle}
        />
        <RouteWithSidebar
          exact
          path={routes?.DeliveredReport?.path}
          component={DeliveredReportPage}
          items={sidebarItems}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          userName={userName}
          copyrightStartYear={copyrightStartYear}
          copyrightTitle={copyrightTitle}
          brandDetails={brandDetails}
          vlinderDetails={vlinderDetails}
          userImage={userImage}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.DeliveredReport?.id,
            whoami,
            "read"
          )}
          brandLogo={brandLogo}
          brandTitle={brandTitle}
        />
        <RouteWithSidebar
          exact
          path={routes?.CommitteMemberReport?.path}
          component={CommitteMemberReportPage}
          items={sidebarItems}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          userName={userName}
          copyrightStartYear={copyrightStartYear}
          copyrightTitle={copyrightTitle}
          brandDetails={brandDetails}
          vlinderDetails={vlinderDetails}
          userImage={userImage}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.CommitteMemberReport?.id,
            whoami,
            "read"
          )}
          brandLogo={brandLogo}
          brandTitle={brandTitle}
        />
        <RouteWithSidebar
          exact
          path={routes?.AboutMe?.path}
          component={AboutMePage}
          items={sidebarItems}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          userName={userName}
          copyrightStartYear={copyrightStartYear}
          copyrightTitle={copyrightTitle}
          brandDetails={brandDetails}
          vlinderDetails={vlinderDetails}
          userImage={userImage}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.AboutMe?.id,
            whoami,
            "read"
          )}
          brandLogo={brandLogo}
          brandTitle={brandTitle}
        />
        <RouteWithSidebar
          exact
          path={routes?.SectionOptionInventory?.path}
          component={SectionOptionInventoryPage}
          items={sidebarItems}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          userName={userName}
          copyrightStartYear={copyrightStartYear}
          copyrightTitle={copyrightTitle}
          brandDetails={brandDetails}
          vlinderDetails={vlinderDetails}
          userImage={userImage}
          brandLogo={brandLogo}
          brandTitle={brandTitle}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.SectionOptionInventory?.id,
            whoami,
            "read"
          )}
        />
        <RouteWithSidebar
          exact
          path={routes?.SectionOptInvReport?.path}
          component={SectionOptInvReportPage}
          items={sidebarItems}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          userName={userName}
          copyrightStartYear={copyrightStartYear}
          copyrightTitle={copyrightTitle}
          brandDetails={brandDetails}
          vlinderDetails={vlinderDetails}
          userImage={userImage}
          brandLogo={brandLogo}
          brandTitle={brandTitle}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.SectionOptInvReport?.id,
            whoami,
            "read"
          )}
        />
        <RouteWithSidebar
          exact
          path={routes?.Presentation?.path}
          component={PresentationPage}
          items={sidebarItems}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          userName={userName}
          copyrightStartYear={copyrightStartYear}
          copyrightTitle={copyrightTitle}
          brandDetails={brandDetails}
          vlinderDetails={vlinderDetails}
          userImage={userImage}
          brandLogo={brandLogo}
          brandTitle={brandTitle}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.Presentation?.id,
            whoami,
            "read"
          )}
        />
        <RouteWithSidebar
          exact
          path={routes?.InventoryControl?.path}
          component={InventoryControlPage}
          items={sidebarItems}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          userName={userName}
          copyrightStartYear={copyrightStartYear}
          copyrightTitle={copyrightTitle}
          brandDetails={brandDetails}
          vlinderDetails={vlinderDetails}
          userImage={userImage}
          brandLogo={brandLogo}
          brandTitle={brandTitle}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.InventoryControl?.id,
            whoami,
            "read"
          )}
        />
        <RouteWithSidebar
          exact
          path={routes?.Costume?.path}
          component={CostumePage}
          items={sidebarItems}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          userName={userName}
          copyrightStartYear={copyrightStartYear}
          copyrightTitle={copyrightTitle}
          brandDetails={brandDetails}
          vlinderDetails={vlinderDetails}
          userImage={userImage}
          brandLogo={brandLogo}
          brandTitle={brandTitle}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.Costume?.id,
            whoami,
            "read"
          )}
        />
        <RouteWithSidebar
          exact
          path={routes?.CostumeOpenReg?.path}
          component={CostumeOpenRegPage}
          items={sidebarItems}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          userName={userName}
          copyrightStartYear={copyrightStartYear}
          copyrightTitle={copyrightTitle}
          brandDetails={brandDetails}
          vlinderDetails={vlinderDetails}
          userImage={userImage}
          brandLogo={brandLogo}
          brandTitle={brandTitle}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.CostumeOpenReg?.id,
            whoami,
            "read"
          )}
        />
        <RouteWithSidebar
          exact
          path={routes?.PurchasedCostume?.path}
          component={PurchasedCostumePage}
          items={sidebarItems}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          userName={userName}
          userImage={userImage}
          copyrightStartYear={copyrightStartYear}
          copyrightTitle={copyrightTitle}
          brandDetails={brandDetails}
          vlinderDetails={vlinderDetails}
          userImage={userImage}
          brandLogo={brandLogo}
          brandTitle={brandTitle}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.PurchasedCostume?.id,
            whoami,
            "read"
          )}
        />
        <RouteWithSidebar
          exact
          path={routes?.PurchasedCostumeList?.path}
          component={PurchasedCostumeListPage}
          items={sidebarItems}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          userName={userName}
          userImage={userImage}
          copyrightStartYear={copyrightStartYear}
          copyrightTitle={copyrightTitle}
          brandDetails={brandDetails}
          vlinderDetails={vlinderDetails}
          userImage={userImage}
          brandLogo={brandLogo}
          brandTitle={brandTitle}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.PurchasedCostumeList?.id,
            whoami,
            "read"
          )}
        />
        <RouteWithSidebar
          exact
          path={routes?.PreBookInviteTemplate?.path}
          component={PreBookInviteEmailTemplatePage}
          items={sidebarItems}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          userName={userName}
          userImage={userImage}
          copyrightStartYear={copyrightStartYear}
          copyrightTitle={copyrightTitle}
          brandDetails={brandDetails}
          vlinderDetails={vlinderDetails}
          userImage={userImage}
          brandLogo={brandLogo}
          brandTitle={brandTitle}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.PreBookInviteTemplate?.id,
            whoami,
            "read"
          )}
        />
        <RouteWithSidebar
          exact
          path={routes?.PartialRemainderEmailTemplate?.path}
          component={PartialRemainderEmailTemplatePage}
          items={sidebarItems}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          userName={userName}
          userImage={userImage}
          copyrightStartYear={copyrightStartYear}
          copyrightTitle={copyrightTitle}
          brandDetails={brandDetails}
          vlinderDetails={vlinderDetails}
          userImage={userImage}
          brandLogo={brandLogo}
          brandTitle={brandTitle}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.PartialRemainderEmailTemplate?.id,
            whoami,
            "read"
          )}
        />
        <RouteWithSidebar
          exact
          path={routes?.UnPaidRemainderEmailTemplate?.path}
          component={UnPaidRemainderEmailTemplatePage}
          items={sidebarItems}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          userName={userName}
          userImage={userImage}
          copyrightStartYear={copyrightStartYear}
          copyrightTitle={copyrightTitle}
          brandDetails={brandDetails}
          vlinderDetails={vlinderDetails}
          userImage={userImage}
          brandLogo={brandLogo}
          brandTitle={brandTitle}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.UnPaidRemainderEmailTemplate?.id,
            whoami,
            "read"
          )}
        />
        <RouteWithSidebar
          exact
          path={routes?.UserList?.path}
          component={UsersListPage}
          items={sidebarItems}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          userName={userName}
          copyrightStartYear={copyrightStartYear}
          copyrightTitle={copyrightTitle}
          brandDetails={brandDetails}
          vlinderDetails={vlinderDetails}
          userImage={userImage}
          brandLogo={brandLogo}
          brandTitle={brandTitle}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.UserList?.id,
            whoami,
            "read"
          )}
        />
        <RouteWithSidebar
          exact
          path={routes?.BlackListUsers?.path}
          component={BlackListUserListPage}
          items={sidebarItems}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          userName={userName}
          copyrightStartYear={copyrightStartYear}
          copyrightTitle={copyrightTitle}
          brandDetails={brandDetails}
          vlinderDetails={vlinderDetails}
          userImage={userImage}
          brandLogo={brandLogo}
          brandTitle={brandTitle}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.BlackListUsers?.id,
            whoami,
            "read"
          )}
        />
        <RouteWithSidebar
          exact
          path={routes?.BlockListUsers?.path}
          component={BlockListUserListPage}
          items={sidebarItems}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          userName={userName}
          copyrightStartYear={copyrightStartYear}
          copyrightTitle={copyrightTitle}
          brandDetails={brandDetails}
          vlinderDetails={vlinderDetails}
          userImage={userImage}
          brandLogo={brandLogo}
          brandTitle={brandTitle}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.BlockListUsers?.id,
            whoami,
            "read"
          )}
        />
        <RouteWithSidebar
          exact
          path={routes?.MasqueraderList?.path}
          component={MasqueraderListPage}
          items={sidebarItems}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          userName={userName}
          copyrightStartYear={copyrightStartYear}
          copyrightTitle={copyrightTitle}
          brandDetails={brandDetails}
          vlinderDetails={vlinderDetails}
          userImage={userImage}
          brandLogo={brandLogo}
          brandTitle={brandTitle}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.MasqueraderList?.id,
            whoami,
            "read"
          )}
        />
        <RouteWithSidebar
          exact
          path={routes?.DeletedBox?.path}
          component={DeletedBoxPage}
          items={sidebarItems}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          userName={userName}
          copyrightStartYear={copyrightStartYear}
          copyrightTitle={copyrightTitle}
          brandDetails={brandDetails}
          vlinderDetails={vlinderDetails}
          userImage={userImage}
          brandLogo={brandLogo}
          brandTitle={brandTitle}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.DeletedBox?.id,
            whoami,
            "read"
          )}
        />

        <RouteWithSidebar
          exact
          path={routes?.DeletedViewBox?.path}
          component={DeletedViewBoxPage}
          items={sidebarItems}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          userName={userName}
          copyrightStartYear={copyrightStartYear}
          copyrightTitle={copyrightTitle}
          brandDetails={brandDetails}
          vlinderDetails={vlinderDetails}
          userImage={userImage}
          brandLogo={brandLogo}
          brandTitle={brandTitle}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.DeletedViewBox?.id,
            whoami,
            "read"
          )}
        />
        <RouteWithSidebar
          exact
          path={routes?.CommitteList?.path}
          component={CommitteListPage}
          items={sidebarItems}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          userName={userName}
          copyrightStartYear={copyrightStartYear}
          copyrightTitle={copyrightTitle}
          brandDetails={brandDetails}
          vlinderDetails={vlinderDetails}
          userImage={userImage}
          brandLogo={brandLogo}
          brandTitle={brandTitle}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.CommitteList?.id,
            whoami,
            "read"
          )}
        />
        <RouteWithSidebar
          exact
          path={routes.CashierList?.path}
          component={CashierListPage}
          items={sidebarItems}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          userName={userName}
          copyrightStartYear={copyrightStartYear}
          copyrightTitle={copyrightTitle}
          brandDetails={brandDetails}
          vlinderDetails={vlinderDetails}
          userImage={userImage}
          brandLogo={brandLogo}
          brandTitle={brandTitle}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.CashierList?.id,
            whoami,
            "read"
          )}
        />
        <RouteWithSidebar
          exact
          path={routes?.PresentationList?.path}
          component={PresentationListPage}
          items={sidebarItems}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          userName={userName}
          copyrightStartYear={copyrightStartYear}
          copyrightTitle={copyrightTitle}
          brandDetails={brandDetails}
          vlinderDetails={vlinderDetails}
          userImage={userImage}
          brandLogo={brandLogo}
          brandTitle={brandTitle}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.PresentationList?.id,
            whoami,
            "read"
          )}
        />
        <RouteWithSidebar
          exact
          path={routes?.InviteMasqueraderPurchase?.path}
          component={InviteMasqueraderPage}
          items={sidebarItems}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          copyrightStartYear={copyrightStartYear}
          copyrightTitle={copyrightTitle}
          brandDetails={brandDetails}
          vlinderDetails={vlinderDetails}
          userImage={userImage}
          userName={userName}
          brandLogo={brandLogo}
          brandTitle={brandTitle}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.InviteMasqueraderPurchase?.id,
            whoami,
            "read"
          )}
        />
        <RouteWithSidebar
          exact
          path={routes?.InviteCommitteMember?.path}
          component={SendInviteCommiteePage}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          items={sidebarItems}
          copyrightStartYear={copyrightStartYear}
          copyrightTitle={copyrightTitle}
          brandDetails={brandDetails}
          vlinderDetails={vlinderDetails}
          userImage={userImage}
          userName={userName}
          brandLogo={brandLogo}
          brandTitle={brandTitle}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.InviteCommitteMember?.id,
            whoami,
            "read"
          )}
        />
        <RouteWithSidebar
          exact
          path={routes?.ViewBoxDetails?.path}
          component={ViewBoxDetailPage}
          items={sidebarItems}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          userName={userName}
          copyrightStartYear={copyrightStartYear}
          copyrightTitle={copyrightTitle}
          brandDetails={brandDetails}
          vlinderDetails={vlinderDetails}
          userImage={userImage}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.ViewBoxDetails?.id,
            whoami,
            "read"
          )}
          brandLogo={brandLogo}
          brandTitle={brandTitle}
        />
        <RouteWithSidebar
          exact
          path={routes?.ViewMasqBoxDetails?.path}
          component={ViewMasqBoxDetailsPage}
          items={sidebarItems}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          userName={userName}
          copyrightStartYear={copyrightStartYear}
          copyrightTitle={copyrightTitle}
          brandDetails={brandDetails}
          vlinderDetails={vlinderDetails}
          userImage={userImage}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.ViewMasqBoxDetails?.id,
            whoami,
            "read"
          )}
          brandLogo={brandLogo}
          brandTitle={brandTitle}
        />
        <RouteWithSidebar
          exact
          path={routes?.ChangeCostumeOptions?.path}
          component={ChangeCostumeOptionsPage}
          items={sidebarItems}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          userName={userName}
          copyrightStartYear={copyrightStartYear}
          copyrightTitle={copyrightTitle}
          brandDetails={brandDetails}
          vlinderDetails={vlinderDetails}
          userImage={userImage}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.ChangeCostumeOptions?.id,
            whoami,
            "read"
          )}
          brandLogo={brandLogo}
          brandTitle={brandTitle}
        />
        <RouteWithSidebar
          exact
          path={routes?.SwitchSection?.path}
          component={SwitchSectionPage}
          items={sidebarItems}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          userName={userName}
          copyrightStartYear={copyrightStartYear}
          copyrightTitle={copyrightTitle}
          brandDetails={brandDetails}
          vlinderDetails={vlinderDetails}
          userImage={userImage}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.SwitchSection?.id,
            whoami,
            "read"
          )}
          brandLogo={brandLogo}
          brandTitle={brandTitle}
        />
        <RouteWithSidebar
          exact
          path={routes?.DiscountInvite?.path}
          component={DiscountedMasqueraderPage}
          items={sidebarItems}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          userName={userName}
          copyrightStartYear={copyrightStartYear}
          copyrightTitle={copyrightTitle}
          brandDetails={brandDetails}
          vlinderDetails={vlinderDetails}
          userImage={userImage}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.DiscountInvite?.id,
            whoami,
            "read"
          )}
          brandLogo={brandLogo}
          brandTitle={brandTitle}
        />
        <RouteWithSidebar
          exact
          path={routes?.CreateMasqBox?.path}
          component={CreateMasqBoxPage}
          items={sidebarItems}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          userName={userName}
          copyrightStartYear={copyrightStartYear}
          copyrightTitle={copyrightTitle}
          brandDetails={brandDetails}
          vlinderDetails={vlinderDetails}
          userImage={userImage}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.CreateMasqBox?.id,
            whoami,
            "read"
          )}
          brandLogo={brandLogo}
          brandTitle={brandTitle}
        />
        <RouteWithSidebar
          exact
          path={routes?.ZeroPaymentReport?.path}
          component={ZeroPaymentReportPage}
          items={sidebarItems}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          userName={userName}
          copyrightStartYear={copyrightStartYear}
          copyrightTitle={copyrightTitle}
          brandDetails={brandDetails}
          vlinderDetails={vlinderDetails}
          userImage={userImage}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.ZeroPaymentReport?.id,
            whoami,
            "read"
          )}
          brandLogo={brandLogo}
          brandTitle={brandTitle}
        />
        <RouteWithSidebar
          exact
          path={routes?.BandRouteMap?.path}
          component={BandRouteMapPage}
          items={sidebarItems}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          userName={userName}
          copyrightStartYear={copyrightStartYear}
          copyrightTitle={copyrightTitle}
          brandDetails={brandDetails}
          vlinderDetails={vlinderDetails}
          userImage={userImage}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.BandRouteMap?.id,
            whoami,
            "read"
          )}
          brandLogo={brandLogo}
          brandTitle={brandTitle}
        />
        <RouteWithSidebar
          exact
          path={routes?.BandRouteLiveMap?.path}
          component={BandRouteLiveMapPage}
          items={sidebarItems}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          userName={userName}
          copyrightStartYear={copyrightStartYear}
          copyrightTitle={copyrightTitle}
          brandDetails={brandDetails}
          vlinderDetails={vlinderDetails}
          userImage={userImage}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.BandRouteLiveMap?.id,
            whoami,
            "read"
          )}
          brandLogo={brandLogo}
          brandTitle={brandTitle}
        />
        <RouteWithSidebar
          exact
          path={routes?.PartialPaymentReport?.path}
          component={PartialPaymentReportPage}
          items={sidebarItems}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          userName={userName}
          copyrightStartYear={copyrightStartYear}
          copyrightTitle={copyrightTitle}
          brandDetails={brandDetails}
          vlinderDetails={vlinderDetails}
          userImage={userImage}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.PartialPaymentReport?.id,
            whoami,
            "read"
          )}
          brandLogo={brandLogo}
          brandTitle={brandTitle}
        />
        <RouteWithSidebar
          exact
          path={routes?.FullPaymentReport?.path}
          component={FullPaymentReportPage}
          items={sidebarItems}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          userName={userName}
          copyrightStartYear={copyrightStartYear}
          copyrightTitle={copyrightTitle}
          brandDetails={brandDetails}
          vlinderDetails={vlinderDetails}
          userImage={userImage}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.FullPaymentReport?.id,
            whoami,
            "read"
          )}
          brandLogo={brandLogo}
          brandTitle={brandTitle}
        />
        <RouteWithSidebar
          exact
          path={routes?.DiscountReport?.path}
          component={DiscountReportPage}
          items={sidebarItems}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          userName={userName}
          copyrightStartYear={copyrightStartYear}
          copyrightTitle={copyrightTitle}
          brandDetails={brandDetails}
          vlinderDetails={vlinderDetails}
          userImage={userImage}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.DiscountReport?.id,
            whoami,
            "read"
          )}
          brandLogo={brandLogo}
          brandTitle={brandTitle}
        />
        <RouteWithSidebar
          exact
          path={routes?.CouponReport?.path}
          component={CouponReportPage}
          items={sidebarItems}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          userName={userName}
          copyrightStartYear={copyrightStartYear}
          copyrightTitle={copyrightTitle}
          brandDetails={brandDetails}
          vlinderDetails={vlinderDetails}
          userImage={userImage}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.CouponReport?.id,
            whoami,
            "read"
          )}
          brandLogo={brandLogo}
          brandTitle={brandTitle}
        />
        <RouteWithSidebar
          exact
          path={routes?.SalesReport?.path}
          component={SalesReportPage}
          items={sidebarItems}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          userName={userName}
          copyrightStartYear={copyrightStartYear}
          copyrightTitle={copyrightTitle}
          brandDetails={brandDetails}
          vlinderDetails={vlinderDetails}
          userImage={userImage}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.SalesReport?.id,
            whoami,
            "read"
          )}
          brandLogo={brandLogo}
          brandTitle={brandTitle}
        />
        <RouteWithSidebar
          exact
          path={routes?.MasqueraderReport?.path}
          component={MasqueraderReportPage}
          items={sidebarItems}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          userName={userName}
          copyrightStartYear={copyrightStartYear}
          copyrightTitle={copyrightTitle}
          brandDetails={brandDetails}
          vlinderDetails={vlinderDetails}
          userImage={userImage}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.SalesReport?.id,
            whoami,
            "read"
          )}
          brandLogo={brandLogo}
          brandTitle={brandTitle}
        />
        <RouteWithSidebar
          exact
          path={routes?.ManageOpenRegistration?.path}
          component={ManageOpenRegistrationPage}
          items={sidebarItems}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          userName={userName}
          copyrightStartYear={copyrightStartYear}
          copyrightTitle={copyrightTitle}
          brandDetails={brandDetails}
          vlinderDetails={vlinderDetails}
          userImage={userImage}
          brandLogo={brandLogo}
          brandTitle={brandTitle}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.ManageOpenRegistration?.id,
            whoami,
            "read"
          )}
        />
        <RouteWithSidebar
          exact
          path={routes?.PaymentTransaction?.path}
          component={PaymentTransactionPage}
          items={sidebarItems}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          userName={userName}
          copyrightStartYear={copyrightStartYear}
          copyrightTitle={copyrightTitle}
          brandDetails={brandDetails}
          vlinderDetails={vlinderDetails}
          userImage={userImage}
          brandLogo={brandLogo}
          brandTitle={brandTitle}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.PaymentTransaction?.id,
            whoami,
            "read"
          )}
        />
        <RouteWithSidebar
          exact
          path={routes?.PrintLabels?.path}
          component={PrintLabelPage}
          items={sidebarItems}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          userName={userName}
          copyrightStartYear={copyrightStartYear}
          copyrightTitle={copyrightTitle}
          brandDetails={brandDetails}
          vlinderDetails={vlinderDetails}
          userImage={userImage}
          brandLogo={brandLogo}
          brandTitle={brandTitle}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.PrintLabels?.id,
            whoami,
            "read"
          )}
        />
        <Route exact path="/bulk" component={HolderBatchImportPage} />
        <Route
          exact
          path="/bulk-invite-committe"
          component={HolderBatchCommitteImportPage}
        />
        <Route
          exact
          path="/bulk-committe-list"
          component={BulkImportCommitteListPage}
        />
        <Route
          exact
          path="/import-presentation"
          component={PresentationImportPage}
        />
        <Route
          exact
          path="/bulk-user-list"
          component={BulkImportUserListPage}
        />
        <Route
          exact
          path={routes?.APV2PaymentDetails?.path}
          component={APV2PaymentDetails}
        />
        <Route exact path="/" component={LandingPage} />
        <Route exact path={routes?.SignIn?.path} component={SignInPage} />
        <Route
          exact
          path={routes?.OpenSignIn?.path}
          component={OpenSigninPage}
        />
        <Route
          exact
          path={routes?.OpenUpdateProfile?.path}
          component={OpenUpdateProfile}
        />
        <Route
          exact
          path={routes?.PaymentFailedFAC?.path}
          component={PaymentFailedFACPage}
        />
        <Route
          exact
          path={routes?.ProfileUpdateStepper?.path}
          component={ProfileUpdateStepperPage}
        />
        <Route
          exact
          path={routes?.PaymentSuccess?.path}
          component={PaymentSuccessPage}
        />
        <Route
          exact
          path={routes?.PaymentFailed?.path}
          component={PaymentFailedPage}
        />
        <Route
          exact
          path={routes?.PaymentPending?.path}
          component={PaymentPendingPage}
        />
        {/* <Route
          exact
          path={routes?.OpenRegister?.path}
          component={OpenRegisterPage}
        /> */}
        <Route
          exact
          path={routes?.MasqueraderReport?.path}
          component={MasqueraderReportPage}
        />
        <Route
          exact
          path={routes?.OpenRegister?.path}
          component={OpenRegistrationCostumePage}
        />
        <Route
          exact
          path={routes?.OpenRegisterFlair?.path}
          component={OpenRegistrationCostumePage}
        />
        <Route
          exact
          path={routes?.OpenRegisterElektra?.path}
          component={OpenRegistrationCostumePage}
        />
        <Route
          exact
          path={routes?.OpenRegisterScorch?.path}
          component={OpenRegistrationCostumePage}
        />
        <Route
          exact
          path={routes?.OpenRegisterTravisWorld?.path}
          component={OpenRegistrationCostumePage}
        />
        <Route
          exact
          path={routes?.OpenRegisterEpicCarnival?.path}
          component={OpenRegistrationCostumePage}
        />
        <Route
          exact
          path={routes?.OpenRegisterBattalionMusic?.path}
          component={OpenRegistrationCostumePage}
        />
        <Route
          exact
          path={routes?.OpenRegisterGenx?.path}
          component={OpenRegistrationCostumePage}
        />
        <Route
          exact
          path={routes?.OpenRegisterTriniJungleJuice?.path}
          component={OpenRegistrationCostumePage}
        />
        <Route
          exact
          path={routes?.OpenRegisterIslandCrashers?.path}
          component={OpenRegistrationCostumePage}
        />
        <Route
          exact
          path={routes?.OpenRegisterLil?.path}
          component={OpenRegistrationCostumePage}
        />
        <Route
          exact
          path={routes?.OpenRegisterSolangeGovia?.path}
          component={OpenRegistrationCostumePage}
        />
        <Route
          exact
          path={routes?.OpenRegisterAbsolute?.path}
          component={OpenRegistrationCostumePage}
        />
        <Route
          exact
          path={routes?.OpenRegisterEnergy?.path}
          component={OpenRegistrationCostumePage}
        />
        <Route
          exact
          path={routes?.OpenRegisterHouseOfBachannal?.path}
          component={OpenRegistrationCostumePage}
        />
        <Route
          exact
          path={routes?.OpenRegisterDLux?.path}
          component={OpenRegistrationCostumePage}
        />
        <Route
          exact
          path={routes?.OpenRegisterPunchyPunch?.path}
          component={OpenRegistrationCostumePage}
        />
        <Route
          exact
          path={routes?.OpenRegisterLauraNa?.path}
          component={OpenRegistrationCostumePage}
        />
        <Route
          exact
          path={routes?.OpenRegisterChandraMaharaj?.path}
          component={OpenRegistrationCostumePage}
        />
        <Route
          exact
          path={routes?.OpenRegisterShawnDhanraj?.path}
          component={OpenRegistrationCostumePage}
        />
        <Route
          exact
          path={routes?.OpenRegisterMADJouvert?.path}
          component={OpenRegistrationCostumePage}
        />
        <Route
          exact
          path={routes?.OpenRegisterBikiniCabana?.path}
          component={OpenRegistrationCostumePage}
        />
        <Route
          exact
          path={routes?.OpenRegisterFarmhouse?.path}
          component={OpenRegistrationCostumePage}
        />
        <Route
          exact
          path={routes?.OpenRegisterJCAMFitness?.path}
          component={OpenRegistrationCostumePage}
        />
        <Route
          exact
          path={routes?.OpenRegisterIImagine?.path}
          component={OpenRegistrationCostumePage}
        />
        <Route
          exact
          path={routes?.OpenRegisterKRISP?.path}
          component={OpenRegistrationCostumePage}
        />
        <Route
          exact
          path={routes?.OpenRegisterKristenCamacho?.path}
          component={OpenRegistrationCostumePage}
        />
        <Route
          exact
          path={routes?.OpenRegisterLiveInLove?.path}
          component={OpenRegistrationCostumePage}
        />
        <Route
          exact
          path={routes?.OpenRegisterMarkCorreia?.path}
          component={OpenRegistrationCostumePage}
        />
        <Route
          exact
          path={routes?.OpenRegisterMixedNutz?.path}
          component={OpenRegistrationCostumePage}
        />
        <Route
          exact
          path={routes?.OpenRegisterRoyale?.path}
          component={OpenRegistrationCostumePage}
        />
        <Route
          exact
          path={routes?.OpenRegisterWinerboy?.path}
          component={OpenRegistrationCostumePage}
        />
        <Route
          exact
          path={routes?.OpenRegisterMVP?.path}
          component={OpenRegistrationCostumePage}
        />
        <Route
          exact
          path={routes?.OpenRegisterNatalieFonrose?.path}
          component={OpenRegistrationCostumePage}
        />
        <Route
          exact
          path={routes?.OpenRegisterDavidDewer?.path}
          component={OpenRegistrationCostumePage}
        />
        <Route
          exact
          path={routes?.OpenRegisterTribeU?.path}
          component={OpenRegistrationCostumePage}
        />
        <Route
          exact
          path={routes?.OpenRegisterClubhouse?.path}
          component={OpenRegistrationCostumePage}
        />
        <Route
          exact
          path={routes?.OpenRegisterMidnight?.path}
          component={OpenRegistrationCostumePage}
        />
        <Route
          exact
          path={routes?.OpenRegisterMarieange?.path}
          component={OpenRegistrationCostumePage}
        />
        <Route
          exact
          path={routes?.OpenRegisterLivinLocal?.path}
          component={OpenRegistrationCostumePage}
        />
        <Route
          exact
          path={routes?.OpenRegisterDean?.path}
          component={OpenRegistrationCostumePage}
        />
        <Route
          exact
          path={routes?.OpenRegisterMonique?.path}
          component={OpenRegistrationCostumePage}
        />
        <Route
          exact
          path={routes?.OpenRegisterGerard?.path}
          component={OpenRegistrationCostumePage}
        />
        <Route
          exact
          path={routes?.OpenRegisterMelissa?.path}
          component={OpenRegistrationCostumePage}
        />
        <Route
          exact
          path={routes?.OpenRegisterLana?.path}
          component={OpenRegistrationCostumePage}
        />
        <Route
          exact
          path={routes?.OpenRegisterGail?.path}
          component={OpenRegistrationCostumePage}
        />
        <Route
          exact
          path={routes?.OpenRegisterDwayne?.path}
          component={OpenRegistrationCostumePage}
        />
        <Route exact path={routes?.NotFound?.path} component={NotFoundPage} />
        <Route
          exact
          path={routes?.TokenExpiry?.path}
          component={TokenExpiryPage}
        />
        <Redirect to={routes?.NotFound?.path} />
      </Switch>
    );
  }
  if (appNavigatorState?.matches("GET_PAGE_CONTENT")) {
    return <StateOSD {...(DEFAULT_APP_CONFIG?.defaultLoading || {})} />;
  }
  return null;
};

export const AppNavigator = (): JSX.Element => {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <Suspense
        fallback={<StateOSD {...(DEFAULT_APP_CONFIG?.defaultLoading || {})} />}
      >
        <RouterConfig />
        <SwitchRolesPage />
      </Suspense>
    </BrowserRouter>
  );
};
